import { createSelector } from 'reselect'
import { List, Map } from 'immutable'
import i18next from 'i18next'

import { escapeSpecialSymbols } from 'helpers/patternsRegExp.ts'
import { LEVEL_TYPE_LIBRARY, LEVEL_TYPE_MICROACTION } from 'app/constants/levels'

export const getProtracks = state => state.get('persist').protracks

export const getProtracksPending = createSelector(getProtracks, protracks => protracks.get('pending'))

export const getAllProtracks = createSelector(getProtracks, protracks => protracks.get('all'))

export const hasAvailableProTracks = createSelector(getAllProtracks, proTracks => proTracks.size)

export const getPublicProtracks = createSelector(getProtracks, protracks => protracks.getIn(['public', 'protracks']))

export const getPublishedProtracks = createSelector(getProtracks, protracks => protracks.get('published'))

const getPublishedProTracksLibraries = createSelector(getProtracks, proTracks => proTracks.get('publishedLibraries'))

export const getPublishedProtracksList = createSelector(getPublishedProtracks, protracks =>
  protracks.reduce((all, protrack) => all.push(protrack), List())
)

export const getPublishedProTracksLibrariesList = createSelector(getPublishedProTracksLibraries, proTracks =>
  proTracks.reduce((all, proTrack) => all.push(proTrack), List())
)

export const getSelectedPublicProtrackId = createSelector(getProtracks, protracks =>
  protracks.getIn(['public', 'selected'])
)

export const getProtracksLoaded = createSelector(getProtracks, protracks => protracks.get('loaded'))

export const getProtracksLoadedAll = createSelector(getProtracksLoaded, loaded => loaded.get('all'))

export const getProtracksLoadedCurrent = createSelector(getProtracksLoaded, loaded => loaded.get('current'))

export const getProtracksList = createSelector(getAllProtracks, protracks =>
  protracks.reduce((all, protrack) => all.push(protrack), List())
)

export const getProtracksBySelectedPublicProtrack = createSelector(
  getSelectedPublicProtrackId,
  getProtracksList,
  (id, protracks) => (id ? protracks.filter(protrack => protrack.get('publicProTrackId') === id) : protracks)
)

export const getPublicProtracksList = createSelector(getPublicProtracks, protracks =>
  protracks.reduce((all, protrack) => all.push(protrack), List()).sortBy(item => item.get('id'))
)

export const getPublicProtracksListWithAll = createSelector(getPublicProtracksList, list =>
  list.unshift(Map({ id: 0, name: i18next.t('All') }))
)

export const getProtracksPublicNamesIds = createSelector(getAllProtracks, protracks =>
  protracks.reduce((publicNamesIds, protrack) => {
    return protrack.get('publicProTrackId') ? publicNamesIds.push(protrack.get('publicProTrackId')) : publicNamesIds
  }, List())
)

export const getViewProtrackId = createSelector(getProtracks, protracks => protracks.get('view'))

export const getViewProtrack = createSelector(getAllProtracks, getViewProtrackId, (protracks, id) =>
  id ? protracks.get(id) : null
)

// NOTE: !proTrack added due to error https://github.com/prohabits/prohabits-ui/issues/3767

export const getTypeOfLevelForViewProTrack = createSelector(getViewProtrack, proTrack => {
  if (!proTrack) {
    return null
  }
  if (proTrack.get('levels') > 0) {
    return LEVEL_TYPE_MICROACTION
  }
  if (proTrack.get('libraryLevels') > 0) {
    return LEVEL_TYPE_LIBRARY
  }
  return null
})

export const getViewProtrackName = createSelector(getViewProtrack, protrack => (protrack ? protrack.get('name') : null))

export const getViewProtrackLabel = createSelector(getViewProtrack, protrack =>
  protrack ? protrack.get('label') : null
)

export const getProtracksPagination = createSelector(getProtracks, protracks => protracks.get('pagination'))

export const getProtracksCurrentPage = createSelector(getProtracksPagination, pagination => pagination.get('current'))

export const getProtracksPerPage = createSelector(getProtracksPagination, pagination => pagination.get('perPage'))

export const getProtracksSortCriteria = createSelector(getProtracks, protracks => protracks.get('sort'))

export const getProtracksSortBy = createSelector(getProtracksSortCriteria, sort => sort.get('by'))

export const getProtracksSortDesc = createSelector(getProtracksSortCriteria, sort => sort.get('desc'))

export const getProtracksSortedByActiveUsers = createSelector(
  getProtracksBySelectedPublicProtrack,
  getProtracksSortBy,
  getProtracksSortDesc,
  (list, by, desc) =>
    by === 'activeUsers'
      ? list.sort((prev, next) => (desc ? next.activeUsers - prev.activeUsers : prev.activeUsers - next.activeUsers))
      : list
)

export const getProtracksSortedByLevel = createSelector(
  getProtracksBySelectedPublicProtrack,
  getProtracksSortBy,
  getProtracksSortDesc,
  (list, by, desc) =>
    by === 'levels' ? list.sort((prev, next) => (desc ? next.levels - prev.levels : prev.levels - next.levels)) : list
)

export const getProtracksSortedList = createSelector(
  getProtracksBySelectedPublicProtrack,
  getProtracksSortedByActiveUsers,
  getProtracksSortedByLevel,
  getProtracksSortBy,
  getProtracksSortDesc,
  (list, byActiveUsers, byLevel, by, desc) => {
    if (by) {
      if (by === 'activeUsers') {
        return byActiveUsers
      } else if (by === 'levels') {
        return byLevel
      } else {
        return list.sortBy(
          item => item.get(by),
          (a, b) => {
            if (typeof a === 'string') {
              return desc ? a.toLowerCase().localeCompare(b) : b.toLowerCase().localeCompare(a)
            } else if (typeof a === 'boolean') {
              return desc ? b - a : a - b
            }
          }
        )
      }
    }
    return list
  }
)

export const getProtracksFilterBy = createSelector(getProtracks, protracks => protracks.get('filter'))

export const getProTracksLibrariesFiltersBy = createSelector(getProtracks, proTrack => proTrack.get('filterLibraries'))

export const getProtracksSortedListFiltered = createSelector(
  getProtracksSortedList,
  getProtracksFilterBy,
  (list, filterBy) =>
    filterBy && filterBy.length >= 1
      ? list.filter(
          item => item.get('name') && item.get('name').search(new RegExp(escapeSpecialSymbols(filterBy), 'i')) !== -1
        )
      : list
)

export const getPublishedProtracksListFiltered = createSelector(
  getPublishedProtracksList,
  getProtracksFilterBy,
  (list, filterBy) =>
    filterBy && filterBy.length >= 1
      ? list.filter(
          item => item.get('name') && item.get('name').search(new RegExp(escapeSpecialSymbols(filterBy), 'i')) !== -1
        )
      : list
)

export const getPublishedProTracksLibrariesListFiltered = createSelector(
  getPublishedProTracksLibrariesList,
  getProTracksLibrariesFiltersBy,
  (list, filterBy) =>
    filterBy && filterBy.length >= 1
      ? list.filter(
          item => item.get('name') && item.get('name').search(new RegExp(escapeSpecialSymbols(filterBy), 'i')) !== -1
        )
      : list
)

export const getProtracksListFiltered = createSelector(getProtracksList, getProtracksFilterBy, (list, filterBy) =>
  filterBy && filterBy.length >= 1
    ? list.filter(item => item.get('name').search(new RegExp(filterBy, 'i')) !== -1)
    : list
)

export const getProtracksSortedListFilteredTotal = createSelector(getProtracksSortedListFiltered, list => list.size)

export const getProtracksTotalPages = createSelector(
  getProtracksSortedListFilteredTotal,
  getProtracksPerPage,
  (total, perPage) => Math.ceil(total / perPage)
)

export const getProtracksForCurrentPage = createSelector(
  getProtracksSortedListFiltered,
  getProtracksCurrentPage,
  getProtracksPerPage,
  (all, currentPage, perPage) => {
    return all.skip(currentPage * perPage).take(perPage)
  }
)
